import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { login } from '../services/login';
import { registerUser } from '../services/registerUser';
import { confirmEmail } from '../services/confirmEmail';
import { resetPassword } from '../services/resetPassword';
import type { UserData } from '@/z-shared/lib/types/userData';
import { rootReducer } from '@/app/providers/storeProvider/config/rootReducer';
import { initialState } from '../const/initialState';
import { requestUserInfo } from '../services/requestUserInfo';

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setShowRegistrationFinalize: (state, action: PayloadAction<boolean>) => {
            state.showRegistrationFinalize = action.payload;
        },
        logoutUser: state => {
            state.isLoggedIn = false;
            state.email = undefined;
            state.skipRefresh = true;
            state.showRegistrationFinalize = false;
            state.userInfo = {};
        },
        loginUser: (state, action: PayloadAction<Required<UserData>>) => {
            state.email = action.payload.email;
            state.isLoggedIn = true;
            state.skipRefresh = false;
        },
        updateUserEmail: (state, action: PayloadAction<string>) => {
            state.email = action.payload;
        },
        updateStrategyExampleFlag: (state, { payload }: PayloadAction<boolean>) => {
            state.userInfo.hasSeenTradeStrategySubscription = payload;
        },
        initAuth: state => {
            state.isInited = true;
        },
    },
    extraReducers: builder => {
        builder
            // login
            .addCase(login.pending, state => {
                state.error = undefined;
                state.loading = true;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.expiresAt = action.payload.expiresAt;
                state.isLoggedIn = true;
                state.skipRefresh = false;
                state.email = action.payload.email;
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
                state.isLoggedIn = false;
                state.skipRefresh = true;
                state.email = undefined;
            })
            // register
            .addCase(registerUser.pending, state => {
                state.error = undefined;
                state.loading = true;
            })
            .addCase(registerUser.fulfilled, (state, _) => {
                state.loading = false;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // verify email
            .addCase(confirmEmail.pending, state => {
                state.error = undefined;
                state.loading = true;
            })
            .addCase(confirmEmail.fulfilled, (state, { payload }) => {
                const { showRegistrationFinalize, email } = payload;
                state.loading = false;
                state.showRegistrationFinalize = Boolean(showRegistrationFinalize);
                state.email = email;
                state.isLoggedIn = true;
                state.skipRefresh = false;
            })
            .addCase(confirmEmail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // reset password
            .addCase(resetPassword.pending, state => {
                state.error = undefined;
                state.loading = true;
            })
            .addCase(resetPassword.fulfilled, (state, _) => {
                state.loading = false;
                state.skipRefresh = false;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            .addCase(requestUserInfo.pending, state => {
                state.userInfoLoading = true;
            })
            .addCase(requestUserInfo.fulfilled, (state, action) => {
                const info = action.payload;
                state.userInfo = info;
                state.userInfoLoading = false;
            })
            .addCase(requestUserInfo.rejected, state => {
                state.userInfoLoading = false;
            });
    },
});

export const { actions: authActions } = authSlice;
export const { reducer: authReducer } = authSlice;

authSlice.injectInto(rootReducer);
